import clsx from 'clsx';
import PropTypes from 'prop-types';
import { useMediaQuery } from 'react-responsive';

// Static map of bgColor values to their opacity variants (50% opacity)
// Tailwind v4 requires static classes, so we can't dynamically generate them
const bgColorOpacityMap = {
    'bg-white': 'bg-white/50',
    'bg-oat': 'bg-oat/50',
    'bg-malt': 'bg-malt/50',
    'bg-off-white': 'bg-off-white/50',
    'bg-kale': 'bg-kale/50',
    'bg-lime': 'bg-lime/50',
    'bg-beet': 'bg-beet/50',
    'bg-strawberry': 'bg-strawberry/50',
    'bg-blueberry': 'bg-blueberry/50',
    'bg-cranberry': 'bg-cranberry/50',
    'bg-error': 'bg-error/50',
    'bg-success': 'bg-success/50',
    'bg-info': 'bg-info/50',
    'bg-warning': 'bg-warning/50',
    'bg-grey-light': 'bg-grey-light/50',
    'bg-grey': 'bg-grey/50',
    'bg-plus-membership-gradient': 'bg-plus-membership-gradient',
    'bg-gradient-berry-blend': 'bg-gradient-berry-blend',
};

function BenefitBase({
    bgColor,
    headline,
    horizontalImage,
    horizontalImagePosition,
    layout,
    subheadline,
    verticalImage,
}) {
    const withinHorizontalBreakpoint = useMediaQuery({ maxWidth: 539 });
    // Use static map lookup - fallback to bgColor if not in map (for gradients or custom classes)
    const bgColorWithOpacity = bgColor ? bgColorOpacityMap[bgColor] || bgColor : null;

    if (layout === 'horizontal' || withinHorizontalBreakpoint) {
        return (
            <div
                className={clsx([
                    bgColorWithOpacity,
                    'grid grid-cols-5 items-center justify-between gap-5 overflow-hidden rounded-xl',
                ])}
            >
                <div className="col-span-3 p-10">
                    <h5 className="text-body-sm mb-[2px] font-grotesk-bold">{headline}</h5>
                    {subheadline && <p className="text-body-xs text-grey">{subheadline}</p>}
                </div>
                <div className="relative col-span-2 flex justify-end">
                    <div className={clsx('absolute flex items-center justify-end', horizontalImagePosition)}>
                        {horizontalImage}
                    </div>
                </div>
            </div>
        );
    }

    // vertical layout
    return (
        <div className={clsx([bgColorWithOpacity, 'flex flex-col items-center rounded-xl p-10 text-center'])}>
            <div className="flex h-[120px] items-center justify-end">{verticalImage}</div>
            <div className="p-10">
                <h5 className="mb-[2px] font-grotesk-bold text-body-mobile-sm md:text-body-desktop-md">{headline}</h5>
                {subheadline && <p className="text-body-mobile-xs text-grey md:text-body-desktop-sm">{subheadline}</p>}
            </div>
        </div>
    );
}

BenefitBase.propTypes = {
    bgColor: PropTypes.string,
    headline: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
    horizontalImage: PropTypes.node.isRequired,
    horizontalImagePosition: PropTypes.string,
    layout: PropTypes.oneOf(['horizontal', 'responsive']),
    subheadline: PropTypes.string,
    verticalImage: PropTypes.node,
};

BenefitBase.defaultProps = {
    bgColor: 'bg-white',
    horizontalImagePosition: undefined,
    layout: 'responsive',
    subheadline: undefined,
    verticalImage: null,
};

export default BenefitBase;
